import React, { useCallback, useRef, useState, useEffect } from "react";
import {
  getFlowOfFunds,
  getFlowOfFundsTagLevel,
} from "../../../../docuclipper/api";
import { useDispatch, useSelector } from "react-redux";
import { ReduxState } from "../../../../redux";
import { createAlert } from "src/redux/reducers/Alerts";
import { FlowOfFundsChart } from "./FlowOfFundsChart";
import { FlowOfFundsTable, FlowOfFundsFilterState } from "./FlowOfFundsTable";
import type { ChartFilterChange, SankeyData } from "./FlowOfFundsChart";

function reorganizeNodes(data: any): SankeyData {
  if (!data?.nodes?.length || !Array.isArray(data.links)) {
    return { nodes: [], links: [] };
  }

  const valueByIndex: Record<number, number> = {};
  data.nodes.forEach((_: any, i: number) => (valueByIndex[i] = 0));
  data.links.forEach((link: any) => {
    if (
      link.source != null &&
      link.target != null &&
      data.nodes[link.source] &&
      data.nodes[link.target]
    ) {
      const val = Math.abs(Number(link.value) || 0);
      valueByIndex[link.source] = (valueByIndex[link.source] || 0) + val;
      valueByIndex[link.target] = (valueByIndex[link.target] || 0) + val;
    }
  });

  const incomeNodes = data.nodes
    .map((node: any, index: number) => ({
      node,
      index,
      value: valueByIndex[index] || 0,
    }))
    .filter(({ node }: any) => node.category === "income")
    .sort((a: any, b: any) => b.value - a.value)
    .map(({ node }: any) => node);
  const accountNodes = data.nodes.filter((n: any) => n.category === "account");
  const expenseNodes = data.nodes
    .map((node: any, index: number) => ({
      node,
      index,
      value: valueByIndex[index] || 0,
    }))
    .filter(({ node }: any) => node.category === "expense")
    .sort((a: any, b: any) => b.value - a.value)
    .map(({ node }: any) => node);

  const orderedNodes = [...incomeNodes, ...accountNodes, ...expenseNodes].map(
    (node: any) => ({
      ...node,
      x:
        node.category === "income"
          ? 20
          : node.category === "expense"
            ? 840
            : 400,
    }),
  );

  const nodeIndexMap = Object.fromEntries(
    orderedNodes.map((node: any, index: number) => [node.name, index]),
  );

  const updatedLinks = data.links
    .filter(
      (link: any) =>
        link.source != null &&
        link.target != null &&
        data.nodes[link.source] &&
        data.nodes[link.target],
    )
    .map((link: any) => ({
      ...link,
      source: nodeIndexMap[data.nodes[link.source].name],
      target: nodeIndexMap[data.nodes[link.target].name],
    }));

  return { nodes: orderedNodes, links: updatedLinks };
}

/**
 * FlowOfFunds page - chart and table are independent.
 * Parent only subscribes to JobData and TagLevel (for chart data fetch).
 * Chart does not subscribe to TransactionManager - table updates won't re-render the chart.
 */
export function FlowOfFunds() {
  const dispatch = useDispatch();
  const gridApiRef = useRef<any>(null);

  const { job } = useSelector((state: ReduxState) => state.JobData);
  const { selectedL1: l1, selectedL2: l2 } = useSelector(
    (state: ReduxState) => state.TagLevel,
  );

  const [filter, setFilter] = useState<FlowOfFundsFilterState>({});
  const [data2, setData2] = useState<SankeyData>({ nodes: [], links: [] });
  const [isLoadingFlowData, setIsLoadingFlowData] = useState(false);

  useEffect(() => {
    if (!(job && job.id) && !(l1 && l2)) return;
    setIsLoadingFlowData(true);
    const fn =
      l1 && l2
        ? getFlowOfFundsTagLevel(l1, l2)
        : job && job.id
          ? getFlowOfFunds(job.id)
          : Promise.resolve();
    fn.then((data) => {
      setData2(reorganizeNodes(data));
    })
      .catch((err) => {
        dispatch(
          createAlert({
            message: err.message || "Error getting flow of funds. Please try again.",
            timeout: 0,
            type: "error",
          }),
        );
      })
      .finally(() => setIsLoadingFlowData(false));
  }, [job, l1, l2, dispatch]);

  const handleChartFilterChange = useCallback((changes: ChartFilterChange) => {
    setFilter({
      selectedCategory: changes.selectedCategory,
      selectedAccount: changes.selectedAccount,
      tx1Account: changes.tx1Account,
      tx2Account: changes.tx2Account,
      linkFilterSource: changes.linkFilterSource,
      linkFilterDest: changes.linkFilterDest,
    });
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilter({});
  }, []);

  if (isLoadingFlowData) {
    return (
      <div className="my-2 alert alert-info" role="alert">
        Loading flow of funds data...
      </div>
    );
  }

  if ((data2.links || []).length === 0 || (data2.nodes || []).length === 0) {
    return (
      <div className="my-2 alert alert-warning" role="alert">
        No categorized transactions found. Go to the Categorize tab to assign categories to your transactions, then return here to see the flow of funds.
      </div>
    );
  }

  return (
    <div className="sankey-charts">
      <div style={{ width: "100%" }}>
        <FlowOfFundsChart
          data={data2}
          onChartFilterChange={handleChartFilterChange}
        />
      </div>

      <div style={{ marginTop: "7.5rem" }}>
        <FlowOfFundsTable
          filter={filter}
          data2={data2}
          onClearFilters={handleClearFilters}
          gridApiRef={gridApiRef}
        />
      </div>
    </div>
  );
}

export default FlowOfFunds;
